/**
 * Read-only view of an editing surface while the org is under the relaunch
 * freeze: a banner with the server message, and a wrapper that lets the admin
 * scroll and look but swallows every click, key press, drag and focus before
 * it reaches the editor. Everything underneath keeps rendering as normal.
 *
 * Generic pieces adapted from program-collaboration/EditLockGuard (the edit
 * lock branch): both take `frozen` + `message` props instead of a provider.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

export const EditingFrozenBanner: React.FC<{ frozen: boolean; message: string; sx?: object }> = ({
  frozen,
  message,
  sx
}) => {
  if (!frozen) return null;
  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        px: 2,
        py: 1.25,
        borderRadius: '8px',
        border: '1px solid rgba(247, 148, 32, 0.45)',
        background: 'rgba(247, 148, 32, 0.08)',
        ...sx
      }}
    >
      <LockOutlinedIcon sx={{ fontSize: 18, color: '#F79420', flexShrink: 0 }} />
      <Typography sx={{ fontFamily: 'Inter', fontSize: 13, color: '#FDFDFD', lineHeight: '18px' }}>
        {message}
      </Typography>
    </Box>
  );
};

// Marks UI that stays usable in the read-only view. Put it on portal roots too:
// dialogs/menus render outside the guarded DOM subtree.
export const EDITING_FROZEN_EXEMPT = { 'data-editing-frozen-exempt': true } as const;

const isExempt = (e: React.SyntheticEvent) =>
  Boolean((e.target as HTMLElement)?.closest?.('[data-editing-frozen-exempt]'));

const swallow = (e: React.SyntheticEvent) => {
  if (isExempt(e)) return;
  e.preventDefault();
  e.stopPropagation();
};

// Keys that only scroll. Everything else could edit.
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

export const ReadOnlyWhenFrozen: React.FC<{ frozen: boolean; children: React.ReactNode; sx?: object }> = ({
  frozen,
  children,
  sx
}) => {
  if (!frozen) return <>{children}</>;
  return (
    <Box
      aria-readonly
      sx={{ display: 'contents', ...sx }}
      onClickCapture={swallow}
      onDoubleClickCapture={swallow}
      onMouseDownCapture={swallow}
      onPointerDownCapture={swallow}
      onDragStartCapture={swallow}
      onDropCapture={swallow}
      onPasteCapture={swallow}
      onChangeCapture={swallow}
      onKeyDownCapture={(e: React.KeyboardEvent) => {
        if (!SCROLL_KEYS.has(e.key) || (e.target as HTMLElement).isContentEditable) swallow(e);
      }}
      onFocusCapture={(e: React.FocusEvent) => {
        if (isExempt(e)) return;
        const el = e.target as HTMLElement;
        if (el.matches('input, textarea, select, [contenteditable="true"]')) el.blur();
      }}
    >
      {children}
    </Box>
  );
};
